/**
 * Jours d'absence : l'enfant n'est pas au foyer (garde alternée, vacances,
 * colo, internat, nuit chez les grands-parents).
 *
 * Spec : docs/spec-deux-maisons.md
 *
 * Décision de design centrale : il n'y a AUCUN moteur de règles de garde. La
 * seule vérité est un ENSEMBLE DE DATES. Les formules de garde sont
 * innombrables et rien n'est jamais fixe (vacances, échanges de week-end,
 * changement de rythme en cours d'année) : un motif récurrent qui ferait
 * autorité sur le calcul deviendrait faux, et le parent finirait par combattre
 * son propre paramétrage. Ici il n'existe ni règle ni exception à la règle.
 *
 * « Répéter ce rythme » (projectRhythm) n'échappe pas à ce principe : il
 * n'enregistre pas un motif, il ÉCRIT de vraies dates que l'on peut ensuite
 * retaper une par une comme toutes les autres.
 *
 * Tout ce fichier est pur : aucun réseau, aucun store, aucune dépendance à
 * l'heure courante. Les dates sont des 'YYYY-MM-DD' en heure LOCALE, produites
 * par date.service.
 */

import { addDays } from '@/services/date.service'
import { weekWindowFor } from '@/utils/weeklyTarget'

/** Ensemble de jours d'absence pour UN enfant, indexé par 'YYYY-MM-DD'. */
export type AwayDaySet = ReadonlySet<string>

/** Jours d'absence par enfant. Un enfant absent de la Map n'a aucune absence. */
export type AwayDaysByChild = ReadonlyMap<string, AwayDaySet>

/** L'enfant est-il absent ce jour ? */
export function isAwayOn(awayDays: AwayDaySet | undefined | null, dateStr: string): boolean {
  return !!awayDays?.has(dateStr)
}

/** Variante multi-enfants, pour les chemins qui portent la Map complète. */
export function isChildAwayOn(
  byChild: AwayDaysByChild | undefined | null,
  childId: string,
  dateStr: string,
): boolean {
  return !!byChild?.get(childId)?.has(dateStr)
}

/**
 * Clé de mois d'une date. Le mois est l'unité de chargement des jours
 * d'absence : c'est exactement le pas de navigation des deux calendriers, donc
 * la comptabilité de « ce qui est déjà chargé » tient dans un Set de chaînes.
 */
export function monthKeyOf(dateStr: string): string {
  return dateStr.slice(0, 7)
}

/** Mois couverts par un intervalle de dates, bornes incluses, triés. */
export function monthsBetween(from: string, to: string): string[] {
  const end = monthKeyOf(to)
  // Bornes inversées : liste vide plutôt qu'une boucle jusqu'à la fin des temps.
  if (monthKeyOf(from) > end) return []

  const out: string[] = []
  let year = Number(from.slice(0, 4))
  let month = Number(from.slice(5, 7))
  for (;;) {
    const key = `${year}-${String(month).padStart(2, '0')}`
    out.push(key)
    if (key >= end) return out
    month += 1
    if (month > 12) {
      month = 1
      year += 1
    }
  }
}

/** Premier et dernier jour d'un mois 'YYYY-MM', en dates locales 'YYYY-MM-DD'. */
export function monthBounds(monthKey: string): { start: string; end: string } {
  const year = Number(monthKey.slice(0, 4))
  const month = Number(monthKey.slice(5, 7))
  // Jour 0 du mois suivant = dernier jour de celui-ci. En UTC pour rester pur :
  // on ne manipule ici que des nombres, jamais un instant réel.
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return { start: `${monthKey}-01`, end: `${monthKey}-${String(lastDay).padStart(2, '0')}` }
}

/**
 * Personne n'est là ce jour ? Sert aux surfaces qui agrègent tout le foyer (le
 * bloc « Aujourd'hui » du dashboard) et qui n'ont plus rien à montrer.
 *
 * Un foyer SANS enfant répond false, jamais true. C'est tout l'intérêt de cette
 * fonction : `[].every()` vaut `true` en JavaScript, donc la version naïve
 * traite un foyer vide, ou un rendu arrivé avant le chargement des enfants,
 * comme une journée d'absence générale et masque des blocs à tort.
 */
export function areAllAwayOn(
  childIds: readonly string[],
  isAway: (childId: string) => boolean,
): boolean {
  if (childIds.length === 0) return false
  return childIds.every(isAway)
}

/**
 * Longueur du cycle projeté, en jours. Sept jours si le motif tapé tient dans
 * une semaine, quatorze sinon : c'est ce qui permet à « une semaine sur deux »
 * de fonctionner sans jamais demander au parent de décrire son rythme.
 *
 * `windowStart`/`windowEnd` bornent le motif MONTRÉ par le parent (typiquement
 * la ou les deux semaines qu'il vient de taper), pas les jours marqués : un
 * rythme « rien la semaine A, mercredi la semaine B » doit rester un cycle de
 * 14 jours même si toutes ses absences tombent dans la seconde semaine.
 */
export function cycleLengthFor(windowStart: string, windowEnd: string): 7 | 14 {
  return daysBetween(windowStart, windowEnd) < 7 ? 7 : 14
}

/** Nombre de jours entiers de `from` à `to` (négatif si `to` précède `from`). */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T12:00:00Z`)
  const b = Date.parse(`${to}T12:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) throw new Error(`daysBetween: date invalide '${from}' → '${to}'`)
  return Math.round((b - a) / 86_400_000)
}

export interface ProjectRhythmArgs {
  /** Jours d'absence tapés par le parent, dans la fenêtre montrée. */
  markedDays: readonly string[]
  /** Premier jour de la fenêtre montrée (inclus). */
  windowStart: string
  /** Dernier jour de la fenêtre montrée (inclus). */
  windowEnd: string
  /** Premier jour à projeter (inclus). En pratique le lendemain de windowEnd. */
  projectFrom: string
  /** Dernier jour à projeter (inclus). */
  projectUntil: string
  /** Longueur du cycle en jours, choisie par le parent (#365). Absente, elle
   *  se déduit de la fenêtre (cycleLengthFor). */
  cycleDays?: number
}

/**
 * « Répéter ce rythme » : reprend le motif tapé dans la fenêtre et le rejoue
 * jusqu'à `projectUntil`, en retournant de VRAIES dates.
 *
 * Le cycle est calé sur `windowStart`, donc un motif de 14 jours reste en phase
 * avec la semaine que le parent a montrée. Les dates retournées sont triées et
 * dédoublonnées ; la fenêtre d'origine n'est jamais réémise (elle est déjà
 * enregistrée), ce qui rend la fonction sûre à rejouer.
 *
 * Retourne un tableau vide si le motif est vide ou si la plage est nulle, pour
 * que l'appelant n'ait aucun cas particulier à traiter.
 */
export function projectRhythm(args: ProjectRhythmArgs): string[] {
  const { markedDays, windowStart, windowEnd, projectFrom, projectUntil } = args
  if (markedDays.length === 0) return []
  if (daysBetween(projectFrom, projectUntil) < 0) return []

  const cycle = args.cycleDays ?? cycleLengthFor(windowStart, windowEnd)

  // Position de chaque jour marqué dans le cycle, modulo positif : un motif
  // peut légitimement contenir des jours antérieurs à windowStart si le parent
  // a tapé en remontant.
  const offsets = new Set<number>()
  for (const day of markedDays) {
    offsets.add(((daysBetween(windowStart, day) % cycle) + cycle) % cycle)
  }

  const out: string[] = []
  const total = daysBetween(projectFrom, projectUntil)
  for (let i = 0; i <= total; i++) {
    const date = addDays(projectFrom, i)
    const offset = ((daysBetween(windowStart, date) % cycle) + cycle) % cycle
    if (offsets.has(offset)) out.push(date)
  }
  return out
}

export const CYCLE_WEEKS = [1, 2, 3, 4] as const
export type CycleWeeks = (typeof CYCLE_WEEKS)[number]

/**
 * La durée de cycle à proposer d'après les jours marqués sur le calendrier
 * affiché (#365) : la plus courte, d'une à quatre semaines, pour laquelle ce
 * que le parent a tapé se répète tel quel, à partir de la semaine du premier
 * jour marqué. Une seule semaine marquée ne prouve aucun cycle : on propose
 * alors une semaine sur deux, le cas de la garde alternée. Ce n'est qu'une
 * proposition, le parent la voit et la change.
 *
 * `shown` : les jours affichés, dans l'ordre ; `isMarked` dit lesquels sont
 * des absences.
 */
export function suggestCycleWeeks(
  shown: readonly string[],
  isMarked: (date: string) => boolean,
  weekStartIndex: number,
): CycleWeeks {
  const firstMarked = shown.find(isMarked)
  if (!firstMarked) return 2
  const from = weekWindowFor(firstMarked, weekStartIndex).start
  const inView = new Set(shown)
  for (const k of CYCLE_WEEKS) {
    let consistent = true
    let witnessed = false
    for (const date of shown) {
      if (date < from) continue
      const later = addDays(date, 7 * k)
      if (!inView.has(later)) continue
      const a = isMarked(date)
      if (a !== isMarked(later)) { consistent = false; break }
      if (a) witnessed = true
    }
    if (consistent && witnessed) return k
  }
  return 2
}

export interface RepeatPlan {
  /** Premier jour du motif : le début de la semaine du premier jour marqué. */
  patternStart: string
  /** Premier jour rejoué : le lendemain du dernier jour marqué. */
  from: string
  /** Dates à écrire, triées. */
  dates: string[]
}

/**
 * « Répéter ce rythme » avec un cycle choisi (#365). Le motif est la première
 * période du cycle, à partir de la semaine du premier jour marqué ; il se
 * rejoue du lendemain du dernier jour marqué jusqu'à `until` inclus. Partir du
 * dernier jour marqué respecte ce que le parent a déjà retouché sur le
 * calendrier affiché (un échange de week-end dans la deuxième période).
 */
export function planRepeat(args: {
  shown: readonly string[]
  isMarked: (date: string) => boolean
  cycleWeeks: number
  weekStartIndex: number
  until: string
}): RepeatPlan | null {
  const marked = args.shown.filter(args.isMarked)
  if (!marked.length) return null
  const patternStart = weekWindowFor(marked[0]!, args.weekStartIndex).start
  const cycleDays = 7 * args.cycleWeeks
  const patternEnd = addDays(patternStart, cycleDays - 1)
  const pattern = marked.filter(d => d <= patternEnd)
  const from = addDays(marked[marked.length - 1]!, 1)
  const dates = projectRhythm({
    markedDays: pattern,
    windowStart: patternStart,
    windowEnd: patternEnd,
    projectFrom: from,
    projectUntil: args.until,
    cycleDays,
  })
  return { patternStart, from, dates }
}

/**
 * Le premier jour de présence APRÈS `date` : le jour du retour. null quand
 * l'enfant reste absent sur tout l'horizon regardé (par défaut la fenêtre que
 * le service charge à l'avance), ce que l'appelant dit « retour pas prévu ».
 */
export function nextPresentDay(
  awayDays: AwayDaySet | undefined | null,
  date: string,
  horizonDays = 200,
): string | null {
  for (let i = 1; i <= horizonDays; i++) {
    const day = addDays(date, i)
    if (!isAwayOn(awayDays, day)) return day
  }
  return null
}

/** Les jours d'une période, bornes incluses, quel que soit l'ordre des deux
 *  taps. */
export function daysInRange(a: string, b: string): string[] {
  const [from, to] = a <= b ? [a, b] : [b, a]
  const out: string[] = []
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d)
  return out
}

/**
 * Nombre de jours de présence dans une plage (bornes incluses). Sert aux
 * surfaces qui doivent relativiser un total, par exemple un taux de complétion
 * dont les jours d'absence ne doivent pas gonfler le dénominateur.
 */
export function presentDaysBetween(
  awayDays: AwayDaySet | undefined | null,
  from: string,
  to: string,
): number {
  const span = daysBetween(from, to)
  if (span < 0) return 0
  let count = 0
  for (let i = 0; i <= span; i++) {
    if (!isAwayOn(awayDays, addDays(from, i))) count++
  }
  return count
}
